import React, { useState, useEffect } from 'react';
import { X, Route, Loader2, AlertTriangle, MapPin, Check } from 'lucide-react';
import { toast } from 'sonner';
import { MapContainer, TileLayer, CircleMarker, Tooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DAY_COLORS = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#06b6d4', '#ec4899', '#84cc16', '#ef4444', '#a855f7', '#78716c'];

const fmtDate = (d) =>
  d ? new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) : '—';

// Phase 1 round re-plan: read-only route ordering + day-split preview.
export default function RoundReplanModal({ initialRound, allRounds, onClose }) {
  const [selectedRounds, setSelectedRounds] = useState(initialRound ? [initialRound] : []);
  const [days, setDays] = useState(0); // 0 = auto (max 24 jobs/day)
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmDay, setConfirmDay] = useState(null);
  const [applyingDay, setApplyingDay] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!selectedRounds.length) { setData(null); return; }
    let stale = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem('streakfree_token');
        const res = await fetch(
          `${API_URL}/api/optimizer/replan?rounds=${encodeURIComponent(selectedRounds.join(','))}&days=${days}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || 'Failed');
        }
        const d = await res.json();
        if (!stale) setData(d);
      } catch (e) {
        if (!stale) setError(e.message || 'Could not build the re-plan.');
      } finally {
        if (!stale) setLoading(false);
      }
    })();
    return () => { stale = true; };
  }, [selectedRounds, days, refreshKey]);

  const applyDay = async (g) => {
    const moves = g.jobs.filter(j => j.flagged && j.job_id && j.group_date)
      .map(j => ({ job_id: j.job_id, new_date: j.group_date }));
    if (!moves.length) return;
    setApplyingDay(g.day);
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(`${API_URL}/api/optimizer/replan/apply`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ moves }),
      });
      if (!res.ok) throw new Error('apply failed');
      const d = await res.json();
      toast.success(`Day ${g.day}: ${d.applied} customer${d.applied !== 1 ? 's' : ''} re-anchored`);
      setConfirmDay(null);
      setRefreshKey(k => k + 1);
    } catch {
      toast.error('Could not apply the day — please try again');
    } finally {
      setApplyingDay(null);
    }
  };

  const toggleRound = (name) =>
    setSelectedRounds((prev) => prev.includes(name) ? prev.filter(r => r !== name) : [...prev, name]);

  const allPins = data ? data.groups.flatMap(g => g.jobs.map(j => ({ ...j, day: g.day }))) : [];

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-end sm:items-center justify-center sm:p-4">
      <div className="bg-white w-full sm:max-w-2xl sm:rounded-2xl h-[94vh] sm:h-[90vh] flex flex-col overflow-hidden rounded-t-2xl" data-testid="round-replan-modal">
        {/* Header */}
        <div className="flex-shrink-0 bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <Route size={20} />
              <div>
                <h2 className="font-bold">Round Re-plan</h2>
                <p className="text-white/80 text-[11px]">Route order from {data?.base?.postcode || 'your base'} — preview only, nothing moves</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-xl" data-testid="replan-close-btn"><X size={20} /></button>
          </div>
          {/* Round pickers */}
          <div className="flex gap-1.5 mt-2.5 overflow-x-auto pb-0.5">
            {allRounds.map((name) => (
              <button
                key={name}
                onClick={() => toggleRound(name)}
                data-testid={`replan-round-${name}`}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
                  selectedRounds.includes(name) ? 'bg-white text-emerald-700' : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                {name}
              </button>
            ))}
          </div>
          {/* Days slider */}
          <div className="flex items-center gap-3 mt-2.5">
            <button
              onClick={() => setDays(0)}
              data-testid="replan-auto-btn"
              className={`px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
                days === 0 ? 'bg-white text-emerald-700' : 'bg-white/15 text-white hover:bg-white/25'
              }`}
            >
              Auto
            </button>
            <span className="text-xs font-semibold whitespace-nowrap" data-testid="replan-days-label">
              {days === 0
                ? (data ? `${data.days} day${data.days !== 1 ? 's' : ''} · max 24/day` : 'max 24/day')
                : `Split into ${days} day${days !== 1 ? 's' : ''}`}
            </span>
            <input
              type="range" min="1" max="10" value={days === 0 ? (data?.days || 1) : days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="flex-1 accent-white"
              data-testid="replan-days-slider"
            />
            {data && (
              <span className="text-xs text-white/85 whitespace-nowrap" data-testid="replan-totals">
                {data.total_jobs} jobs · £{data.total_value} · {data.total_miles} mi
              </span>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="flex flex-col items-center justify-center py-16 text-zinc-500">
              <Loader2 size={26} className="animate-spin mb-2" />
              <p className="text-sm">Plotting the optimal route…</p>
            </div>
          )}
          {!loading && error && <div className="m-4 bg-red-50 text-red-600 px-4 py-3 rounded-xl text-sm">{error}</div>}

          {!loading && !error && data && data.total_jobs > 0 && (
            <>
              {/* Map */}
              <div className="h-64 border-b border-zinc-200">
                <MapContainer
                  center={[data.base.lat, data.base.lng]} zoom={13}
                  style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}
                >
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
                  <CircleMarker center={[data.base.lat, data.base.lng]} radius={9}
                    pathOptions={{ color: '#18181b', fillColor: '#18181b', fillOpacity: 1 }}>
                    <Tooltip permanent direction="top">Base</Tooltip>
                  </CircleMarker>
                  {allPins.map((p) => (
                    <CircleMarker key={p.customer_id} center={[p.lat, p.lng]} radius={7}
                      pathOptions={{ color: '#fff', weight: 1, fillColor: DAY_COLORS[(p.day - 1) % 10], fillOpacity: 0.9 }}>
                      <Tooltip>#{p.order} {p.name} · Day {p.day}</Tooltip>
                    </CircleMarker>
                  ))}
                </MapContainer>
              </div>
              <div className="px-3 py-1.5 flex gap-3 text-[11px] text-zinc-600 border-b border-zinc-100 flex-wrap">
                {data.groups.map((g) => (
                  <span key={g.day} className="flex items-center gap-1">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: DAY_COLORS[(g.day - 1) % 10] }} />
                    Day {g.day} ({g.job_count})
                  </span>
                ))}
                {data.unpinned?.length > 0 && (
                  <span className="text-amber-600">{data.unpinned.length} without map pins (excluded)</span>
                )}
              </div>

              {/* Day groups */}
              {data.groups.map((g) => (
                <div key={g.day} data-testid={`replan-day-${g.day}`}>
                  <div className="sticky top-0 bg-zinc-50 border-y border-zinc-200 px-4 py-2 flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: DAY_COLORS[(g.day - 1) % 10] }} />
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-zinc-800 truncate">
                        Day {g.day}
                        {(() => {
                          const dates = [...new Set(Object.values(g.majority_dates || {}))].sort();
                          return dates.length ? ` → ${dates.map(fmtDate).join(' / ')}` : '';
                        })()}
                      </p>
                      <p className="text-xs text-zinc-500">{g.job_count} jobs · £{g.value} · {g.miles} mi</p>
                    </div>
                    {g.flagged_count > 0 && (
                      <span className="ml-auto flex items-center gap-1.5">
                        <span className="text-[11px] font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                          {g.flagged_count} off-date
                        </span>
                        {confirmDay === g.day ? (
                          <>
                            <button
                              onClick={() => applyDay(g)}
                              disabled={applyingDay === g.day}
                              data-testid={`replan-apply-confirm-${g.day}`}
                              className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-emerald-600 text-white disabled:opacity-50 flex items-center gap-1"
                            >
                              {applyingDay === g.day ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
                              Confirm {g.flagged_count} moves
                            </button>
                            <button onClick={() => setConfirmDay(null)} className="text-[11px] px-2 py-1 rounded-full bg-zinc-200 text-zinc-600">Cancel</button>
                          </>
                        ) : (
                          <button
                            onClick={() => setConfirmDay(g.day)}
                            data-testid={`replan-apply-${g.day}`}
                            className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-indigo-600 text-white"
                          >
                            Apply this day
                          </button>
                        )}
                      </span>
                    )}
                  </div>
                  {g.jobs.map((j) => (
                    <div key={j.customer_id} className={`px-4 py-2.5 border-b border-zinc-100 flex items-start gap-3 ${j.flagged ? 'bg-amber-50' : ''}`}
                      data-testid={`replan-job-${j.customer_id}`}>
                      <span className="w-7 h-7 rounded-full text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={{ backgroundColor: DAY_COLORS[(g.day - 1) % 10] }}>
                        {j.order}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-2">
                          <p className="font-semibold text-zinc-900 text-sm truncate">{j.name}</p>
                          <span className="text-xs text-zinc-400 whitespace-nowrap">+{j.leg_mi} mi</span>
                        </div>
                        <p className="text-xs text-zinc-500 truncate flex items-center gap-1">
                          <MapPin size={11} className="flex-shrink-0" /> {j.address}
                        </p>
                        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600">{j.round}</span>
                          {j.frequency && <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-500">{j.frequency}</span>}
                          <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${j.flagged ? 'bg-amber-200 text-amber-900' : 'bg-emerald-50 text-emerald-700'}`}>
                            due {fmtDate(j.next_due)}
                          </span>
                          {j.flagged && (
                            <span className="text-[10px] text-amber-700 flex items-center gap-0.5">
                              <AlertTriangle size={10} /> group runs {fmtDate(j.group_date)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </>
          )}

          {!loading && !error && data && data.total_jobs === 0 && (
            <p className="text-center text-sm text-zinc-500 py-16">No recurring jobs with map pins in the selected round(s).</p>
          )}
        </div>
      </div>
    </div>
  );
}
